import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Sparkles } from "lucide-react";
import { http, inr, offerText } from "@/lib/api";

const WHERE = { package: "Holidays", flight: "Flights", hotel: "Hotels", cab: "Taxi" };

export const useOffers = () => useQuery({ queryKey: ["offers"], queryFn: () => http.get("/offers").then((r) => r.data) });

const copy = (code) => {
  const done = () => toast.success(`${code} copied — apply it at checkout`);
  const fail = () => toast.message(`Use code ${code} at checkout`);
  if (!navigator.clipboard?.writeText) return fail();
  navigator.clipboard.writeText(code).then(done, fail);
};

export const OffersStrip = () => {
  const { data = [] } = useOffers();
  if (!data.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-8" data-testid="offers-section">
      <p className="eyebrow">Festive offers</p>
      <h2 className="font-display mt-2 text-4xl font-bold text-[#0D1B2A] sm:text-5xl">Diwali & seasonal deals</h2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((o, i) => (
          <div key={o.code} className="lift relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0D1B2A] to-[#1B263B] p-6 text-white fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`offer-card-${o.code}`}>
            <Sparkles className="absolute right-5 top-5 h-6 w-6 text-[#F4A261]" />
            <p className="font-display text-4xl font-bold text-[#F4A261]">{offerText(o)}</p>
            <p className="mt-2 text-sm text-slate-300">{o.description}</p>
            <p className="mt-3 text-xs text-slate-400">
              {o.applies_to?.length ? o.applies_to.map((a) => WHERE[a]).join(", ") : "All bookings"}
              {o.min_amount ? ` · min. ${inr(o.min_amount)}` : ""}{o.max_discount && o.discount_type === "percent" ? ` · up to ${inr(o.max_discount)}` : ""} · till {o.valid_to}
            </p>
            <button onClick={() => copy(o.code)} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-dashed border-[#F4A261] px-4 py-2 font-mono text-sm font-bold tracking-wider text-[#F4A261] transition-colors hover:bg-[#F4A261] hover:text-[#0D1B2A]" data-testid={`offer-copy-${o.code}`}>
              {o.code}<Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export const OfferBanner = () => {
  const { data = [] } = useOffers();
  const o = data.find((x) => x.applies_to?.includes("package")) || data[0];
  if (!o) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#0D1B2A] px-6 py-4 text-white" data-testid="offer-banner">
      <p className="text-sm"><b className="text-[#F4A261]">{offerText(o)}</b> — {o.description}</p>
      <button onClick={() => copy(o.code)} className="rounded-lg border border-dashed border-[#F4A261] px-3 py-1.5 font-mono text-sm font-bold text-[#F4A261]" data-testid="offer-banner-code">Use {o.code}</button>
    </div>
  );
};
